import { Children, createContext, useContext, useLayoutEffect, useRef, useState } from 'react';

/**
 * Defaults for every ItemRow inside it, so a step can set them once instead of on each row. A row's own props win.
 */
export const ItemRowSettings = createContext<{ minItemWidth?: number }>({});

interface ItemRowProps {
    /** One to three items, shown left to right in the order given. */
    children: React.ReactNode;
    /**
     * With a single child, how many of totalTableColumns it covers. Ignored with more than one child.
     * Omit it to let a single child fill the row.
     */
    columnSpan?: 1 | 2 | 3;
    /**
     * With a single child, how many columns the row is divided into for columnSpan; defaults to 2, so a span of 1 is
     * the same width as each field in a 2-item row. Ignored with more than one child.
     */
    totalTableColumns?: 1 | 2 | 3;
    /**
     * Narrowest an item may get (px). When the row is too narrow to give each column this much, the items stack one per
     * line at full width (a spanned single child goes full width too). Omit to never stack; falls back to ItemRowSettings.
     */
    minItemWidth?: number;
}

/** Puts its children on one row and splits the row evenly between them (full, halves, or thirds). */
export const ItemRow = ({ children, columnSpan, totalTableColumns = 2, minItemWidth }: ItemRowProps) => {
    const settings = useContext(ItemRowSettings);
    const stackBelowItemWidth = minItemWidth ?? settings.minItemWidth;

    // Drops false/null children, so a hidden field doesn't take a column
    const items = Children.toArray(children);

    if (import.meta.env.DEV && items.length > 3) {
        console.warn(`ItemRow takes up to 3 items; got ${items.length}.`);
    }
    if (import.meta.env.DEV && columnSpan !== undefined && items.length > 1) {
        console.warn(`ItemRow's columnSpan only applies to a single child; got ${items.length}.`);
    }

    const columnGap = 24;
    const isSpanned = items.length === 1 && columnSpan !== undefined;
    // A spanned child lines up with rows of totalTableColumns items, so it stacks when they would
    const columnCount = isSpanned ? totalTableColumns : items.length;

    // Measured (not a CSS breakpoint) because the row's width depends on the modal and step around it, not the screen
    const rowRef = useRef<HTMLDivElement>(null);
    const [isStacked, set_isStacked] = useState(false);
    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row || stackBelowItemWidth === undefined || columnCount < 2) {
            set_isStacked(false);
            return;
        }
        const neededWidth = columnCount * stackBelowItemWidth + (columnCount - 1) * columnGap;
        const observer = new ResizeObserver(([entry]) => set_isStacked(entry.contentRect.width < neededWidth));
        observer.observe(row);
        return () => observer.disconnect();
    }, [stackBelowItemWidth, columnCount]);

    // The row div stays the same element in every layout, so the ResizeObserver keeps watching it
    let rowStyle: React.CSSProperties;
    let itemStyle: React.CSSProperties;
    if (isStacked) {
        rowStyle = { display: 'flex', flexDirection: 'column' };
        itemStyle = { minWidth: 0 };
    } else if (isSpanned) {
        // A spanned single child sits in a grid of totalTableColumns, so its edges line up with rows of that many items
        rowStyle = {
            display: 'grid',
            gridTemplateColumns: `repeat(${totalTableColumns}, minmax(0, 1fr))`,
            alignItems: 'start',
            columnGap,
        };
        itemStyle = { gridColumn: `span ${Math.min(columnSpan, totalTableColumns)}`, minWidth: 0 };
    } else {
        // Every item grows from 0, so each gets the same share of the row whatever its content. Top-aligned, so an error
        // or helper line under one field adds height below it instead of shifting the fields beside it down
        rowStyle = { display: 'flex', alignItems: 'flex-start', columnGap };
        itemStyle = { flex: '1 1 0', minWidth: 0 };
    }

    return (
        <div
            ref={rowRef}
            style={rowStyle}
        >
            {items.map((item, index) => (
                <div
                    key={index}
                    style={itemStyle}
                >
                    {item}
                </div>
            ))}
        </div>
    );
};
